import React, { useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const FILTER_FIELDS = [
    'claimNo',
    'chassisNo',
    'dealerName',
    'countryName',
    'gtNumber',
    'dealerCurrency',
    'claimTotalAmount',
    'customerPaidTotalAmount',
    'warrantyPartTotal',
    'warrantyLaborTotal',
    'warrantyOtherCostTotal',
    'towingTotal',
    'roadsideAssistanceTotal',
    'otherLaborTotal',
    'otherPartTotal'
];

const formatText = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    return value;
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VehicleWarrantyClaimsReportSumTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const makeTextColumn = (field, headerKey, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body: body || ((row) => formatText(row?.[field])),
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const makeAmountColumn = (field, headerKey, options = {}) => {
        const { sortable = true, minWidth = '170px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            bodyClassName: 'text-right',
            headerClassName: 'text-right',
            body: (row) => formatAmount(row?.[field]),
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const tableFilters = useMemo(() => {
        const filters = {};
        FILTER_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS
            };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            FILTER_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const handleSort = (e) => onSortChange?.(e);

    const rows = useMemo(() => (Array.isArray(items) ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` })) : []), [items, lazyParams.first]);

    const columns = [
        makeTextColumn('claimNo', 'vehicleWarrantyClaimsReportSum.columns.claimNo', { minWidth: '150px' }),
        makeTextColumn('chassisNo', 'vehicleWarrantyClaimsReportSum.columns.chassisNo', { minWidth: '190px' }),
        makeTextColumn('dealerName', 'vehicleWarrantyClaimsReportSum.columns.dealerName', {
            minWidth: '250px',
            body: (rowData) => createTruncatedTextForTable(String(formatText(rowData?.dealerName)), 30)
        }),
        makeTextColumn('countryName', 'vehicleWarrantyClaimsReportSum.columns.countryName', { minWidth: '140px' }),
        makeTextColumn('gtNumber', 'vehicleWarrantyClaimsReportSum.columns.gtNumber', { minWidth: '120px' }),
        makeTextColumn('dealerCurrency', 'vehicleWarrantyClaimsReportSum.columns.dealerCurrency', { minWidth: '130px' }),
        makeAmountColumn('claimTotalAmount', 'vehicleWarrantyClaimsReportSum.columns.claimTotalAmount', { minWidth: '180px' }),
        makeAmountColumn('customerPaidTotalAmount', 'vehicleWarrantyClaimsReportSum.columns.customerPaidTotalAmount', { minWidth: '210px' }),
        makeAmountColumn('warrantyPartTotal', 'vehicleWarrantyClaimsReportSum.columns.warrantyPartTotal', { minWidth: '190px' }),
        makeAmountColumn('warrantyLaborTotal', 'vehicleWarrantyClaimsReportSum.columns.warrantyLaborTotal', { minWidth: '190px' }),
        makeAmountColumn('warrantyOtherCostTotal', 'vehicleWarrantyClaimsReportSum.columns.warrantyOtherCostTotal', { minWidth: '220px' }),
        makeAmountColumn('towingTotal', 'vehicleWarrantyClaimsReportSum.columns.towingTotal', { minWidth: '160px' }),
        makeAmountColumn('roadsideAssistanceTotal', 'vehicleWarrantyClaimsReportSum.columns.roadsideAssistanceTotal', { minWidth: '190px' }),
        makeAmountColumn('otherLaborTotal', 'vehicleWarrantyClaimsReportSum.columns.otherLaborTotal', { minWidth: '180px' }),
        makeAmountColumn('otherPartTotal', 'vehicleWarrantyClaimsReportSum.columns.otherPartTotal', { minWidth: '180px' })
    ];

    return (
        <StickyScrollTable deps={[items, totalCount, loading, activeFilters]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey="vehicle-warranty-claims-report-sum"
                columns={columns}
                value={rows}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 10}
                onPage={onPage}
                dataKey="_rowId"
                loading={loading}
                filterDisplay="row"
                filters={tableFilters}
                onFilter={handleTableFilter}
                emptyMessage={th('common.noRecords')}
                rowsPerPageOptions={[10, 20, 50, 100]}
                tableStyle={{ minWidth: '80rem' }}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                sortMode="single"
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
};

export default VehicleWarrantyClaimsReportSumTable;
